import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Paper,
  Alert,
  TextField,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import { useAuth } from "../contexts/AuthContext";
import { apiService } from "../services/api";
import DrWeedyLogo from "../assets/DrWeedyLogo";
import SubscriptionSection from "../components/SubscriptionSection";

// ─── estilos ─────────────────────────────────────────────────────────────────

const containerSx = {
  minHeight: "100vh",
  background: "linear-gradient(135deg, #1a313c 0%, #04090a 100%)",
  color: "#ffffff",
  fontFamily: "Inter, system-ui, sans-serif",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: { xs: "16px", md: "32px 24px" },
};

const sectionCardSx = {
  width: "100%",
  maxWidth: 620,
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: "20px",
  padding: { xs: "20px", md: "28px" },
  color: "#ffffff",
  mb: 3,
};

const sectionTitleSx = {
  fontFamily: "Oxanium, sans-serif",
  fontWeight: 700,
  fontSize: { xs: "1.1rem", md: "1.25rem" },
  mb: 2.5,
  display: "flex",
  alignItems: "center",
  gap: "10px",
};

const iconBgSx = {
  width: 36,
  height: 36,
  borderRadius: "10px",
  background: "rgba(226,114,91,0.15)",
  border: "1px solid rgba(226,114,91,0.3)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const fieldLabelSx = {
  color: "rgba(255,255,255,0.5)",
  fontSize: "11px",
  fontWeight: 600,
  letterSpacing: "0.08em",
  textTransform: "uppercase" as const,
  mb: 0.5,
};

const fieldValueSx = {
  color: "#ffffff",
  fontSize: "15px",
  fontWeight: 500,
};

const primaryBtnSx = {
  background: "linear-gradient(135deg, #E2725B 0%, #CC3B2A 100%)",
  color: "#fff",
  borderRadius: "0.875rem",
  textTransform: "none" as const,
  fontWeight: 700,
  fontSize: "0.875rem",
  px: 3,
  py: 1.25,
  "&:disabled": { opacity: 0.5, color: "#fff" },
};

const ghostBtnSx = {
  color: "rgba(255,255,255,0.8)",
  borderColor: "rgba(255,255,255,0.2)",
  borderRadius: "0.875rem",
  textTransform: "none" as const,
  fontSize: "0.875rem",
  fontWeight: 600,
  button: {
    alignItems: "center",
    border: "1px solid rgba(255,255,255,0.2)",
  },
};

const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    color: "#ffffff",
    borderRadius: "0.875rem",
    backgroundColor: "rgba(255,255,255,0.04)",
    "& fieldset": { borderColor: "rgba(255,255,255,0.15)" },
    "&:hover fieldset": { borderColor: "rgba(255,255,255,0.3)" },
    "&.Mui-focused fieldset": { borderColor: "#E2725B" },
  },
  "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.5)" },
  "& .MuiInputLabel-root.Mui-focused": { color: "#E2725B" },
};

// ─── componente ──────────────────────────────────────────────────────────────

/**
 * Tela centralizada de gestão de conta do médico: perfil, segurança e
 * assinatura. No perfil, nome, e-mail e conselho são só leitura; telefone e
 * especialidade são editáveis.
 */
export default function AccountManagementPage() {
  const navigate = useNavigate();
  const { user, setSubscription } = useAuth();

  // Conselho só leitura: número e tipo são a identidade que assina os
  // documentos, e o PUT /doctor/profile só grava a especialidade.
  const displayCrm = (user?.doctorProfile?.council_number as string) ?? "";
  const displayCrmType = (user?.doctorProfile?.council_type as string) ?? "CRM";

  // display states to show changes immediately
  const [displayName, setDisplayName] = useState(user?.name ?? "");
  const [displaySpeciality, setDisplaySpeciality] = useState(
    (user?.doctorProfile?.speciality as string) ?? "",
  );
  const [displayPhone, setDisplayPhone] = useState(user?.phone ?? "");

  useEffect(() => {
    setDisplayName(user?.name ?? "");
    setDisplaySpeciality((user?.doctorProfile?.speciality as string) ?? "");
    setDisplayPhone(user?.phone ?? "");
  }, [user]);

  // estado do campo de especialidade
  const [editingSpeciality, setEditingSpeciality] = useState(false);

  const [specialityValue, setSpecialityValue] = useState(displaySpeciality);
  const [specialityBusy, setSpecialityBusy] = useState(false);
  const [specialityError, setSpecialityError] = useState("");
  const [specialitySuccess, setSpecialitySuccess] = useState("");

  const handleSaveSpeciality = async () => {
    if (!specialityValue.trim()) {
      setSpecialityError("A especialidade não pode ficar em branco.");
      return;
    }
    try {
      setSpecialityBusy(true);
      setSpecialityError("");
      setSpecialitySuccess("");
      await apiService.updateDoctorProfile({
        speciality: specialityValue.trim(),
      });
      setSpecialitySuccess("Especialidade atualizada com sucesso.");
      setDisplaySpeciality(specialityValue.trim());
      setEditingSpeciality(false);
      setTimeout(() => setSpecialitySuccess(""), 5000);
    } catch (err) {
      setSpecialityError(
        err instanceof Error ? err.message : "Erro ao atualizar especialidade.",
      );
      setTimeout(() => setSpecialityError(""), 5000);
    } finally {
      setSpecialityBusy(false);
    }
  };

  const handleCancelEditSpeciality = () => {
    setSpecialityValue(displaySpeciality);
    setSpecialityError("");
    setEditingSpeciality(false);
  };

  // estado do campo de telefone
  const [editingPhone, setEditingPhone] = useState(false);
  const [phoneValue, setPhoneValue] = useState(displayPhone);
  const [phoneBusy, setPhoneBusy] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [phoneSuccess, setPhoneSuccess] = useState("");

  const handleSavePhone = async () => {
    if (!phoneValue.trim()) {
      setPhoneError("O telefone não pode ficar em branco.");
      return;
    }
    try {
      setPhoneBusy(true);
      setPhoneError("");
      setPhoneSuccess("");
      await apiService.updateDoctorPhone({ phone: phoneValue.trim() });
      setPhoneSuccess("Telefone atualizado com sucesso.");
      setDisplayPhone(phoneValue.trim());
      setEditingPhone(false);
      setTimeout(() => setPhoneSuccess(""), 5000);
    } catch (err) {
      setPhoneError(
        err instanceof Error ? err.message : "Erro ao atualizar telefone.",
      );
      setTimeout(() => setPhoneError(""), 5000);
    } finally {
      setPhoneBusy(false);
    }
  };

  const handleCancelEditPhone = () => {
    setPhoneValue(displayPhone);
    setPhoneError("");
    setEditingPhone(false);
  };

  return (
    <Box sx={containerSx}>
      {/* ── cabeçalho ─────────────────────────────────────────────────────── */}
      <Box
        sx={{
          width: "100%",
          maxWidth: 620,
          mb: 4,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <DrWeedyLogo />
        <Button
          sx={ghostBtnSx}
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/chat")}
        >
          Voltar ao chat
        </Button>
      </Box>

      <Typography
        variant="h4"
        sx={{
          fontFamily: "Oxanium, sans-serif",
          fontWeight: 800,
          fontSize: { xs: "1.6rem", md: "2rem" },
          mb: 1,
          width: "100%",
          maxWidth: 620,
          letterSpacing: "-0.02em",
        }}
      >
        Minha conta
      </Typography>
      <Typography
        sx={{
          color: "rgba(255,255,255,0.55)",
          fontSize: "15px",
          mb: 4,
          width: "100%",
          maxWidth: 620,
        }}
      >
        Gerencie seus dados pessoais, segurança e assinatura em um só lugar.
      </Typography>

      {/* ── seção: perfil ──────────────────────────────────────────────────── */}
      <Paper
        sx={sectionCardSx}
        component="section"
        aria-label="Dados do perfil"
      >
        <Typography sx={sectionTitleSx}>
          <Box sx={iconBgSx}>
            <PersonOutlineIcon sx={{ color: "#E2725B", fontSize: "18px" }} />
          </Box>
          Perfil
        </Typography>

        {specialitySuccess && (
          <Alert
            severity="success"
            sx={{ mb: 2, borderRadius: "12px" }}
            onClose={() => setSpecialitySuccess("")}
          >
            {specialitySuccess}
          </Alert>
        )}
        {specialityError && (
          <Alert
            severity="error"
            sx={{ mb: 2, borderRadius: "12px" }}
            onClose={() => setSpecialityError("")}
          >
            {specialityError}
          </Alert>
        )}

        {phoneSuccess && (
          <Alert
            severity="success"
            sx={{ mb: 2, borderRadius: "12px" }}
            onClose={() => setPhoneSuccess("")}
          >
            {phoneSuccess}
          </Alert>
        )}
        {phoneError && (
          <Alert
            severity="error"
            sx={{ mb: 2, borderRadius: "12px" }}
            onClose={() => setPhoneError("")}
          >
            {phoneError}
          </Alert>
        )}

        {/* nome */}
        <Box sx={{ mb: 2.5 }}>
          <Typography sx={fieldLabelSx}>Nome completo</Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mt: 0.5,
            }}
          >
            <Typography sx={fieldValueSx}>{displayName || "—"}</Typography>
          </Box>
        </Box>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.07)", mb: 2.5 }} />

        {/* e-mail */}
        <Box sx={{ mb: 2.5 }}>
          <Typography sx={fieldLabelSx}>E-mail</Typography>
          <Typography sx={{ ...fieldValueSx, mt: 0.5 }}>
            {user?.email ?? "—"}
          </Typography>
        </Box>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.07)", mb: 2.5 }} />

        {/* telefone */}
        <Box sx={{ mb: displayCrm ? 2.5 : 0 }}>
          <Typography sx={fieldLabelSx}>Telefone (Contato)</Typography>
          {editingPhone ? (
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", mt: 1 }}>
              <TextField
                value={phoneValue}
                onChange={(e) => setPhoneValue(e.target.value)}
                size="small"
                fullWidth
                autoFocus
                disabled={phoneBusy}
                placeholder="(00) 00000-0000"
                sx={textFieldSx}
                InputProps={{
                  endAdornment: phoneBusy ? (
                    <InputAdornment position="end">
                      <CircularProgress size={16} sx={{ color: "#E2725B" }} />
                    </InputAdornment>
                  ) : undefined,
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSavePhone();
                  if (e.key === "Escape") handleCancelEditPhone();
                }}
                inputProps={{ "aria-label": "Editar telefone" }}
              />
              <IconButton
                onClick={handleSavePhone}
                disabled={phoneBusy}
                size="small"
                aria-label="Confirmar alteração de telefone"
                sx={{
                  color: "#5BC47A",
                  backgroundColor: "rgba(91,196,122,0.1)",
                  borderRadius: "10px",
                  "&:hover": { backgroundColor: "rgba(91,196,122,0.2)" },
                }}
              >
                <CheckIcon fontSize="small" />
              </IconButton>
              <IconButton
                onClick={handleCancelEditPhone}
                disabled={phoneBusy}
                size="small"
                aria-label="Cancelar edição de telefone"
                sx={{
                  color: "rgba(255,255,255,0.5)",
                  borderRadius: "10px",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.07)" },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mt: 0.5,
              }}
            >
              <Typography sx={fieldValueSx}>{displayPhone || "—"}</Typography>
              <IconButton
                size="small"
                onClick={() => {
                  setPhoneValue(displayPhone);
                  setPhoneError("");
                  setPhoneSuccess("");
                  setEditingPhone(true);
                }}
                aria-label="Editar telefone"
                sx={{
                  color: "rgba(255,255,255,0.4)",
                  borderRadius: "10px",
                  transition: "all 0.2s",
                  "&:hover": {
                    color: "#E2725B",
                    backgroundColor: "rgba(226,114,91,0.1)",
                  },
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>

        {/* CRM / ID profissional */}
        {displayCrm && (
          <>
            <Divider sx={{ borderColor: "rgba(255,255,255,0.07)", mb: 2.5 }} />
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={fieldLabelSx}>Conselho Profissional</Typography>
              <Typography sx={{ ...fieldValueSx, mt: 0.5 }}>
                {displayCrmType} {displayCrm}
              </Typography>
            </Box>
          </>
        )}

        {/* especialidade */}
        {user?.doctorProfile?.speciality !== undefined && (
          <>
            <Divider sx={{ borderColor: "rgba(255,255,255,0.07)", mb: 2.5 }} />
            <Box>
              <Typography sx={fieldLabelSx}>Especialidade</Typography>
              {editingSpeciality ? (
                <Box
                  sx={{ display: "flex", gap: 1, alignItems: "center", mt: 1 }}
                >
                  <TextField
                    value={specialityValue}
                    onChange={(e) => setSpecialityValue(e.target.value)}
                    size="small"
                    fullWidth
                    autoFocus
                    disabled={specialityBusy}
                    placeholder="Sua especialidade"
                    sx={textFieldSx}
                    InputProps={{
                      endAdornment: specialityBusy ? (
                        <InputAdornment position="end">
                          <CircularProgress
                            size={16}
                            sx={{ color: "#E2725B" }}
                          />
                        </InputAdornment>
                      ) : undefined,
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSaveSpeciality();
                      if (e.key === "Escape") handleCancelEditSpeciality();
                    }}
                    inputProps={{ "aria-label": "Editar especialidade" }}
                  />
                  <IconButton
                    onClick={handleSaveSpeciality}
                    disabled={specialityBusy}
                    size="small"
                    aria-label="Confirmar alteração de especialidade"
                    sx={{
                      color: "#5BC47A",
                      backgroundColor: "rgba(91,196,122,0.1)",
                      borderRadius: "10px",
                      "&:hover": { backgroundColor: "rgba(91,196,122,0.2)" },
                    }}
                  >
                    <CheckIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    onClick={handleCancelEditSpeciality}
                    disabled={specialityBusy}
                    size="small"
                    aria-label="Cancelar edição de especialidade"
                    sx={{
                      color: "rgba(255,255,255,0.5)",
                      borderRadius: "10px",
                      "&:hover": { backgroundColor: "rgba(255,255,255,0.07)" },
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mt: 0.5,
                  }}
                >
                  <Typography sx={fieldValueSx}>
                    {displaySpeciality || "—"}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSpecialityValue(displaySpeciality);
                      setSpecialityError("");
                      setSpecialitySuccess("");
                      setEditingSpeciality(true);
                    }}
                    aria-label="Editar especialidade"
                    sx={{
                      color: "rgba(255,255,255,0.4)",
                      borderRadius: "10px",
                      transition: "all 0.2s",
                      "&:hover": {
                        color: "#E2725B",
                        backgroundColor: "rgba(226,114,91,0.1)",
                      },
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>
          </>
        )}
      </Paper>

      {/* ── seção: segurança ───────────────────────────────────────────────── */}
      <Paper sx={sectionCardSx} component="section" aria-label="Segurança">
        <Typography sx={sectionTitleSx}>
          <Box sx={iconBgSx}>
            <LockOutlinedIcon sx={{ color: "#E2725B", fontSize: "18px" }} />
          </Box>
          Segurança
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: "15px", mb: 0.25 }}>
              Senha
            </Typography>
            <Typography
              sx={{ color: "rgba(255,255,255,0.5)", fontSize: "13px" }}
            >
              Campos de senha nunca são exibidos em texto simples.
            </Typography>
          </Box>
          <Button
            sx={primaryBtnSx}
            onClick={() =>
              navigate("/change-password", { state: { from: "/account" } })
            }
            aria-label="Alterar senha"
            id="btn-change-password"
          >
            Alterar senha
          </Button>
        </Box>
      </Paper>

      {/* ── seção: assinatura ─────────────────────────────────────────────── */}
      <Paper
        sx={{ ...sectionCardSx, p: 0, overflow: "hidden" }}
        component="section"
        aria-label="Assinatura"
      >
        <Box
          sx={{
            px: { xs: 2.5, sm: 3.5, md: 0 },
            pt: { xs: 2.5, sm: 3, md: 0 },
            pb: 0,
          }}
        >
          <Typography sx={sectionTitleSx}>
            <Box sx={iconBgSx}>
              <CreditCardOutlinedIcon
                sx={{ color: "#E2725B", fontSize: "18px" }}
              />
            </Box>
            Assinatura
          </Typography>
        </Box>

        {/* SubscriptionSection tem seu próprio Paper interno — integramos
            dentro do card desta seção apenas com padding. */}
        <Box
          sx={{
            px: { xs: 2.5, sm: 3.5, md: 0 },
            pb: { xs: 2.5, sm: 3, md: 0 },
            "& > .MuiPaper-root": {
              // remove o Paper duplo dentro do SubscriptionSection
              background: "transparent",
              border: "none",
              borderRadius: 0,
              padding: 0,
            },
          }}
        >
          <SubscriptionSection />
        </Box>
      </Paper>
    </Box>
  );
}
